import React, {
  forwardRef,
  Fragment,
  useImperativeHandle,
  useRef,
} from 'react';
import { Link, List, ListItem } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import createBasePath from '@stack/helpers/lib/createBasePath';
import NextLink from 'next/link';
interface BrandListProps<T> {
  list: { [key: string]: T[] };
  labelKey: keyof T;
  valueKey: keyof T;
  clickBrand: (brandSlug: T) => void;
  imageKey?: keyof T;
  style?: React.CSSProperties;
  current_brand?: string;
  renderItem?: (val: T) => React.ReactNode;
  itemRight?: boolean;
}

export interface MainListHandle {
  scrollToSection: (key: string) => void;
  container: HTMLDivElement | null;
}
/* eslint-disable @typescript-eslint/no-explicit-any */
const MainList = forwardRef<MainListHandle, BrandListProps<any>>(
  (
    {
      list,
      current_brand,
      labelKey,
      valueKey,
      imageKey,
      clickBrand,
      style,
      renderItem,
      itemRight,
    }: BrandListProps<any>,
    ref
  ) => {
    const { basePath } = createBasePath({ webView: false, type: 'cars' });
    const brandListContainerRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
      scrollToSection(key: string) {
        const container = brandListContainerRef.current;
        if (container) {
          const targetElement = container.querySelector(`#section_${key}`);
          if (targetElement) {
            container.scrollTo({
              top:
                targetElement.getBoundingClientRect().top +
                container.scrollTop -
                container.getBoundingClientRect().top,
              behavior: 'smooth',
            });
          }
        }
      },
      container: brandListContainerRef.current,
    }));

    const renderItemView = (item: any, isSelected?: boolean) => {
      const label = item[labelKey] as string;
      const image = imageKey ? (item[imageKey] as string) : undefined;
      if (renderItem) return renderItem(item);
      return (
        <Stack
          sx={{
            height: '43px',
            alignItems: 'center',
            gap: 5,
            flexDirection: 'row',
            width: '100%',
          }}
        >
          {image && (
            <img
              className="new-car-brand-image"
              src={image}
              alt={label}
              style={{ width: 30 }}
            />
          )}
          <Link
            component={NextLink}
            href={`${basePath}/${item?.car_brand_slug}`}
            sx={{
              fontSize: '.875rem',
              fontWeight: 700,
              color: isSelected ? '#31ACBF' : 'text.primary',
              flexGrow: 1,
            }}
            underline="none"
            onClick={(event) => {
              event.preventDefault();
            }}
          >
            {label}
          </Link>
        </Stack>
      );
    };

    return (
      <Stack
        className="main"
        id="brand-scrollactive"
        ref={brandListContainerRef}
        sx={{
          ...style,
          position: 'absolute',
          maxHeight: '100%',
          width: itemRight ? 'calc(100% - 45px)' : 'calc(100% - 34px)',
          overflowY: 'auto',
          paddingRight: itemRight ? 5 : 0,
          ...(itemRight ? { left: '46px' } : { left: '0' }),
          '::-webkit-scrollbar': {
            width: '6px',
            ...(!itemRight && { display: 'none' }),
          },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: '#31ACBF',
            borderRadius: '50px',
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: '#31ACBF',
          },
        }}
        divider={<Divider sx={{ marginY: '0 !important' }} />}
      >
        {Object.keys(list).map((key) => (
          <List
            key={`desktop_brand_container_group_${key}`}
            id={`section_${key}`}
            sx={{
              p: 0,
            }}
          >
            {list[key].map((item, index) => {
              const value = item[valueKey] as string;
              const isSelected = current_brand === value;
              return (
                <Fragment
                  key={`desktop_brand_container_children_${item[labelKey]}_${index}`}
                >
                  <ListItem
                    sx={{
                      backgroundColor: isSelected ? '#E8E8E8' : 'transparent',
                      cursor: 'pointer',
                      padding: '12px 20px',
                      width: '100%',
                    }}
                    className={`brand-container-group-item ${
                      isSelected ? 'active' : ''
                    }`}
                    onClick={(event) => {
                      event.preventDefault();
                      clickBrand(item);
                    }}
                  >
                    {renderItemView(item, isSelected)}
                  </ListItem>
                  {index < list[key].length - 1 && (
                    <Divider sx={{ marginY: '0 !important' }} />
                  )}
                </Fragment>
              );
            })}
          </List>
        ))}
      </Stack>
    );
  }
);

MainList.displayName = 'MainList';
export default MainList;
